import { useContext, useEffect, useState, type ReactNode } from 'react'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded'
import { Button, StatusChip, TextInput, Avatar } from '../../Common'
import { Select } from '../../Common/Select'
import { MemberControlsEditor } from '../../Common/MemberControlsEditor'
import {
  setMemberControls,
  subscribeToControlProfiles,
} from '../../../services/memberControlsService'
import type { ControlProfile, MemberControls, MemberControlsSettings } from '../../../types/memberControls'
import { WorkspaceContext } from '../../../context/workspaceContextCore'
import { TIMELINE_BOARD_KEYS, TIMELINE_BOARD_LABELS, type TimelineBoardKey } from '../../../types/schema'
import type { WorkspaceRole } from '../../../types/workspace'
import { useNls } from '../../../nls/nlsProvider'
import { roleLabelKey } from '../../../utils/roleLabels'
import './MemberControlsSheet.css'

export type MemberControlsSheetPerson = {
  id: string
  uid: string | null
  email: string
  displayName: string
  role: WorkspaceRole
  boardRoles: Partial<Record<TimelineBoardKey, WorkspaceRole>>
  lastActiveMs: number | null
  mobileNumber?: string | null
  credits: number
  dailyCreditAllowance?: number
  creditsConsumedToday: number
  photoURL: string | null
  controls?: MemberControlsSettings | null
  effectiveControls?: MemberControls | null
}

type MemberControlsSheetProps = {
  member: MemberControlsSheetPerson
  workspaceId?: string
  kind: 'MEMBER' | 'HELPER'
  canManageMembers: boolean
  busyAction: string | null
  onRoleChange: (role: WorkspaceRole) => void
  onBoardPermissionChange?: (board: TimelineBoardKey, role: WorkspaceRole) => void
  onCreditAllot?: (value: string) => void
  onMessage?: () => void
  onRemove?: () => void
  onViewProfile?: () => void
  onControlsSaved?: () => void
  isOwnerViewer?: boolean
  removeDisabled?: boolean
  removeTitle?: string
  removeLabel?: string
  customRole?: { name: string; color: string } | null
  whatsappActions?: ReactNode
  helperControls?: ReactNode
  accessControls?: ReactNode
  mobile?: boolean
  roleLabel?: string
  boardPermissionsLabel?: string
  creditsLabel?: string
}

const ROLE_OPTIONS: WorkspaceRole[] = ['ADMIN', 'EDITOR', 'VIEWER', 'READONLY', 'HELPER']
const BOARD_ROLE_OPTIONS: WorkspaceRole[] = ['ADMIN', 'EDITOR', 'VIEWER', 'READONLY']

const formatRelative = (ms: number | null): string => {
  if (!ms) return 'No activity yet'
  const minutes = Math.max(0, Math.round((Date.now() - ms) / 60_000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`
  return `${Math.round(minutes / 1440)}d ago`
}

const presenceFor = (ms: number | null) => {
  if (!ms) return 'offline'
  const elapsed = Date.now() - ms
  if (elapsed <= 5 * 60_000) return 'online'
  if (elapsed <= 30 * 60_000) return 'away'
  return 'offline'
}

export const MemberControlsSheet = ({
  member,
  workspaceId,
  kind,
  canManageMembers,
  busyAction,
  onRoleChange,
  onBoardPermissionChange,
  onCreditAllot,
  onMessage,
  onRemove,
  onViewProfile,
  onControlsSaved,
  isOwnerViewer = false,
  removeDisabled = false,
  removeTitle,
  removeLabel = 'Remove',
  customRole,
  whatsappActions,
  helperControls,
  accessControls,
  mobile = false,
  roleLabel = 'Workspace role',
  boardPermissionsLabel = 'Board permissions',
  creditsLabel = 'Credits',
}: MemberControlsSheetProps) => {
  const { t } = useNls()
  const workspaceContext = useContext(WorkspaceContext)
  const resolvedWorkspaceId = workspaceId || workspaceContext?.activeWorkspaceId || ''

  const [profiles, setProfiles] = useState<ControlProfile[]>([])
  const [selectedProfileId, setSelectedProfileId] = useState<string>(
    () => member.controls?.profileId ?? '',
  )
  const [overrides, setOverrides] = useState<MemberControls>(
    () => member.controls?.overrides ?? {},
  )
  const [isSavingControls, setIsSavingControls] = useState(false)
  const [controlsSavedFeedback, setControlsSavedFeedback] = useState<string | null>(null)

  const [prevMemberControlsKey, setPrevMemberControlsKey] = useState(
    () => `${member.id}:${member.controls?.profileId ?? ''}`,
  )
  const currentKey = `${member.id}:${member.controls?.profileId ?? ''}`
  if (prevMemberControlsKey !== currentKey) {
    setPrevMemberControlsKey(currentKey)
    setSelectedProfileId(member.controls?.profileId ?? '')
    setOverrides(member.controls?.overrides ?? {})
  }

  useEffect(() => {
    if (!resolvedWorkspaceId) return undefined
    return subscribeToControlProfiles(resolvedWorkspaceId, (fetchedProfiles) => {
      setProfiles(fetchedProfiles)
    })
  }, [resolvedWorkspaceId])

  const selectedProfile = profiles.find((p) => p.id === selectedProfileId) ?? null

  const presence = presenceFor(member.lastActiveMs)
  const contact = member.email || member.mobileNumber || 'No contact on file'

  const isOwnerMember = member.role === 'OWNER'
  const isAdminMember = member.role === 'ADMIN'
  const isRoleDisabled =
    isOwnerMember ||
    (isAdminMember && !isOwnerViewer) ||
    !canManageMembers ||
    busyAction === `role:${member.id}`

  const isBoardRoleDisabled =
    isOwnerMember ||
    (isAdminMember && !isOwnerViewer) ||
    !canManageMembers ||
    !onBoardPermissionChange

  const isControlsDisabled =
    !canManageMembers ||
    (isAdminMember && !isOwnerViewer) ||
    isSavingControls

  const handleSaveControls = async () => {
    const targetUserId = member.uid || member.id
    if (!resolvedWorkspaceId || !targetUserId) return
    setIsSavingControls(true)
    setControlsSavedFeedback(null)
    try {
      await setMemberControls({
        workspaceId: resolvedWorkspaceId,
        targetUserId,
        controls: {
          profileId: selectedProfileId || null,
          overrides,
        },
      })
      setControlsSavedFeedback(t('NLSControlsSavedFeedback'))
      onControlsSaved?.()
    } catch (err) {
      console.warn('[MemberControlsSheet] setMemberControls failed:', err)
      setControlsSavedFeedback(t('NLSControlsSaveFailedFeedback'))
    } finally {
      setIsSavingControls(false)
    }
  }

  const effectiveRemoveDisabled =
    removeDisabled ||
    isOwnerMember ||
    (isAdminMember && !isOwnerViewer) ||
    !canManageMembers ||
    busyAction === `remove-member:${member.id}`

  const effectiveRemoveTitle = removeTitle

  const roleOptions = isOwnerMember
    ? [{ value: 'OWNER' as WorkspaceRole, label: t(roleLabelKey('OWNER')), disabled: true }]
    : ROLE_OPTIONS.map((role) => ({
        value: role,
        label: t(roleLabelKey(role)),
        disabled: kind === 'HELPER' && role !== 'HELPER',
      }))

  const boardRoleOptions = isOwnerMember
    ? [{ value: 'OWNER' as WorkspaceRole, label: t(roleLabelKey('OWNER')), disabled: true }]
    : BOARD_ROLE_OPTIONS.map((role) => ({ value: role, label: t(roleLabelKey(role)) }))

  return <div className={`memberControlsSheet${mobile ? ' memberControlsSheet--mobile' : ''}`} data-testid="member-controls-sheet">
    <section className="invitesSidecarHero">
      <header className="invitesSidecarHeader">
        <div className="invitesSidecarAvatar">
          <Avatar
            photoURL={member.photoURL}
            name={member.displayName}
            size="lg"
            className="w-full h-full"
          />
          <span className={`invitesSidecarPresence is-${presence}`} aria-hidden="true" />
        </div>
        <div className="invitesSidecarIdentity">
          <strong>{member.displayName}</strong>
          <span>{contact}</span>
          <div className="invitesSidecarChips">
            <StatusChip size="sm" tone={presence === 'online' ? 'success' : presence === 'away' ? 'warning' : 'muted'} className={`invitesSidecarPresenceChip is-${presence}`}>{presence}</StatusChip>
            {customRole ? <span className="invitesSidecarCustomChip" style={{ backgroundColor: customRole.color }}>{customRole.name}</span> : null}
            <span className="invitesSidecarLastActive">Active {formatRelative(member.lastActiveMs)}</span>
          </div>
        </div>
      </header>
      <div className="invitesSidecarActions">
        {whatsappActions}
        {onViewProfile ? <Button variant="secondary" size="sm" disabled={!member.uid} onClick={onViewProfile}>View profile</Button> : null}
        {onMessage ? <Button variant="tertiary" size="md" icon={<ChatBubbleOutlineRoundedIcon fontSize="small" />} disabled={!member.uid} onClick={onMessage}>Message</Button> : null}
        {onRemove ? <Button variant="tertiary" tone="danger" size="sm" disabled={effectiveRemoveDisabled} title={effectiveRemoveTitle} onClick={onRemove}><RemoveCircleOutlineRoundedIcon fontSize="small" />{removeLabel}</Button> : null}
      </div>
    </section>
    <section className="invitesSidecarSection">
      <h4>{roleLabel}</h4>
      <Select<WorkspaceRole>
        value={member.role}
        options={roleOptions}
        onChange={onRoleChange}
        ariaLabel="Workspace role"
        disabled={isRoleDisabled}
        trigger="labeledPill"
      />
    </section>
    {isOwnerMember ? (
      <section className="invitesSidecarSection memberControlsSheet__controlsSection" data-testid="member-controls-section">
        <h4>{t('NLSControlsProfilePickerLabel')}</h4>
        <p className="memberControlsOwnerNotice">{t('NLSControlsOwnersHaveNoControls')}</p>
      </section>
    ) : (
      <section className="invitesSidecarSection memberControlsSheet__controlsSection" data-testid="member-controls-section">
        <h4>{t('NLSControlsProfilePickerLabel')}</h4>
        <div className="memberControlsProfilePicker">
          <Select<string>
            trigger="pill"
            value={selectedProfileId}
            options={[
              { value: '', label: t('NLSControlsProfileNone') },
              ...profiles.map((p) => ({ value: p.id || '', label: p.name })),
            ]}
            disabled={isControlsDisabled}
            onChange={(val) => {
              setSelectedProfileId(val)
            }}
            ariaLabel="Control profile"
          />
        </div>

        <MemberControlsEditor
          profileName={selectedProfile?.name}
          profileControls={selectedProfile?.controls}
          controls={overrides}
          onChange={(patch) => {
            setOverrides(patch)
          }}
          onResetControl={(key) => {
            setOverrides((curr) => {
              const next = { ...curr }
              delete next[key]
              return next
            })
          }}
          disabled={isControlsDisabled}
          showAi={kind !== 'HELPER' && member.role !== 'HELPER'}
        />

        <div className="memberControlsActions">
          <Button
            variant="secondary"
            size="sm"
            disabled={isControlsDisabled || isSavingControls}
            onClick={handleSaveControls}
          >
            {isSavingControls ? 'Saving...' : t('NLSControlsSave')}
          </Button>
          {controlsSavedFeedback ? <span className="memberControlsFeedback">{controlsSavedFeedback}</span> : null}
        </div>
      </section>
    )}
    {kind === 'MEMBER' ? <>
      <section className="invitesSidecarSection">
        <h4>{boardPermissionsLabel}</h4>
        <div className="invitesSidecarBoardGrid memberControlsSheet__boardGrid">
          {TIMELINE_BOARD_KEYS.map((board) => <label key={board}>
            <span className="memberControlsSheet__boardLabel">{TIMELINE_BOARD_LABELS[board]}</span>
            <Select<WorkspaceRole>
              value={member.boardRoles[board] ?? member.role}
              options={boardRoleOptions}
              onChange={(role) => onBoardPermissionChange?.(board, role)}
              ariaLabel={`${TIMELINE_BOARD_LABELS[board]} role`}
              disabled={isBoardRoleDisabled || busyAction === `board:${member.id}:${board}`}
              trigger="pill"
            />
          </label>)}
        </div>
      </section>
      <section className="invitesSidecarSection">
        <h4>{creditsLabel}</h4>
        <div className="invitesSidecarCreditsRow">
          <div><span className="invitesSidecarCreditsLabel">Balance</span><strong>{member.credits.toLocaleString()}</strong></div>
          <div><span className="invitesSidecarCreditsLabel">Used today</span><strong>{member.creditsConsumedToday.toLocaleString()}</strong></div>
        </div>
        {onCreditAllot ? <label className="invitesSidecarCreditsField">
          <span>Allot credits (+/-)</span>
          <TextInput
            type="number"
            inputMode="numeric"
            step={1}
            placeholder="0"
            disabled={!canManageMembers || busyAction === `credits:${member.id}`}
            onKeyDown={(event) => { if (event.key === 'Enter') { onCreditAllot(event.currentTarget.value); event.currentTarget.value = '' } }}
            aria-label={`Allot credits to ${member.displayName}`}
          />
        </label> : null}
      </section>
      {accessControls ? (
        <section className="invitesSidecarSection">
          {accessControls}
        </section>
      ) : null}
    </> : helperControls}
  </div>
}
